import { createSlice } from '@reduxjs/toolkit';

import Cookies from 'js-cookie';
import { RootState } from '..';
import {
  createDiagnostic,
  getDiagnosticById,
  getDiagnosticBySubTopicId,
  getDiagnosticBySubmissionId,
  getRecommendation,
  shareWithParent,
} from './DiagnosticApi';
import { ISubmission } from '../SubmissionSlice';
import { IUser } from '../UserSlice';
import { setAuthUserToLocalStorage } from '../../helper';

export interface ILessonPlan {
  topic: string;
  subTopics: Array<{ name: string; noOfHours: number }>;
}

interface IStripeSession {
  id: string;
  url: string;
  customerId: string;
  amount: number;
  status: string;
}

interface IDiagnosticParent {
  name: string;
  phoneNumber: string;
}

interface IHubSpot {
  contactId?: string;
  dealId?: string;
}

export interface IDiagnostic {
  _id?: string;
  submission: ISubmission;
  email: string;
  fullName: string;
  phoneNumber: string;
  grade: string;
  curriculum: string;
  subject: string;
  subTopicId: string;
  strengths?: Array<string>;
  improvements?: Array<string>;
  lessonPlan?: Array<ILessonPlan>;
  recommendedHours?: number;
  stripeSession?: IStripeSession;
  parent?: IDiagnosticParent;
  user?: IUser;
  hubSpot?: IHubSpot;
}

export interface IRecommendation {
  subject: string;
  grade: string[];
  feedback: string;
}

export interface IDiagnosticState {
  status: 'idle' | 'loading' | 'rejected';
  diagnostic: IDiagnostic | undefined;
  recommendationStatus: 'idle' | 'loading' | 'rejected';
  recommendation: IRecommendation | undefined;
  shareStatus: 'idle' | 'loading' | 'rejected';
}

const initialState: IDiagnosticState = {
  status: 'idle',
  diagnostic: undefined,
  recommendationStatus: 'idle',
  recommendation: undefined,
  shareStatus: 'idle',
};

export const DiagnosticSlice = createSlice({
  name: 'diagnostic',
  initialState,
  reducers: {
    resetDiagnostic: (state) => {
      state.diagnostic = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create diagnostic
      .addCase(createDiagnostic.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createDiagnostic.fulfilled, (state, action) => {
        state.status = 'idle';
        state.diagnostic = action.payload.diagnostic;
        if (action.payload.user?.isLeadGenSubmitted) {
          if (action.payload.user.tutopiyaUserId) {
            Cookies.set('tutopiya_user_id', action.payload.user.tutopiyaUserId);
            window.location.reload();
          } else {
            setAuthUserToLocalStorage(action.payload.user);
          }
        }
      })
      .addCase(createDiagnostic.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get diagnostic
      .addCase(getDiagnosticById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getDiagnosticById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.diagnostic = action.payload;
      })
      .addCase(getDiagnosticById.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get diagnostic by submissionId
      .addCase(getDiagnosticBySubmissionId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getDiagnosticBySubmissionId.fulfilled, (state, action) => {
        state.status = 'idle';
        state.diagnostic = action.payload;
      })
      .addCase(getDiagnosticBySubmissionId.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get diagnostic by SubTopicId
      .addCase(getDiagnosticBySubTopicId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getDiagnosticBySubTopicId.fulfilled, (state, action) => {
        state.status = 'idle';
        state.diagnostic = action.payload;
      })
      .addCase(getDiagnosticBySubTopicId.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Recommendation
      .addCase(getRecommendation.pending, (state) => {
        state.recommendationStatus = 'loading';
      })
      .addCase(getRecommendation.fulfilled, (state, action) => {
        state.recommendationStatus = 'idle';
        state.recommendation = action.payload;
      })
      .addCase(getRecommendation.rejected, (state) => {
        state.recommendationStatus = 'rejected';
      })

      // Share With Parent
      .addCase(shareWithParent.pending, (state) => {
        state.shareStatus = 'loading';
      })
      .addCase(shareWithParent.fulfilled, (state, action) => {
        state.shareStatus = 'idle';
        state.diagnostic = action.payload;
      })
      .addCase(shareWithParent.rejected, (state) => {
        state.shareStatus = 'rejected';
      });
  },
});

export const getDiagnosticState = (state: RootState): RootState['diagnostic'] =>
  state.diagnostic;

export const { resetDiagnostic } = DiagnosticSlice.actions;

export * from './DiagnosticApi';
export default DiagnosticSlice.reducer;
